"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { CropData, CroppedImage } from "./crop-utils"
import { generateCropId } from "./crop-utils"

export interface ImageFolder {
  id: string
  name: string
  description?: string
  createdAt: number
  updatedAt: number
  postIds: Set<string>
  posts: Map<string, any> // Store full post data
  croppedImages: Map<string, CroppedImage[]> // postId → array of crops
}

interface FoldersStore {
  folders: Map<string, ImageFolder>
  createFolder: (name: string, description?: string) => string
  deleteFolder: (folderId: string) => void
  renameFolder: (folderId: string, name: string) => void
  addPostToFolder: (folderId: string, postId: string, post?: any) => void
  removePostFromFolder: (folderId: string, postId: string) => void
  isPostInFolder: (folderId: string, postId: string) => boolean
  getPostFolders: (postId: string) => ImageFolder[]
  getFolderPosts: (folderId: string) => any[]
  // Crop management
  addCroppedImage: (folderId: string, postId: string, cropData: CropData, name?: string) => string
  removeCroppedImage: (folderId: string, cropId: string) => void
  updateCroppedImage: (folderId: string, cropId: string, cropData: CropData) => void
  getCroppedImages: (folderId: string, postId: string) => CroppedImage[]
  getAllCroppedImages: (folderId: string) => CroppedImage[]
}

export const useFoldersStore = create<FoldersStore>()(
  persist(
    (set, get) => ({
      folders: new Map(),

      createFolder: (name: string, description?: string) => {
        const id = `folder-${Date.now()}`
        const newFolder: ImageFolder = {
          id,
          name,
          description,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          postIds: new Set(),
          posts: new Map(),
          croppedImages: new Map(),
        }

        set((state) => {
          const newFolders = new Map(state.folders)
          newFolders.set(id, newFolder)
          return { folders: newFolders }
        })

        return id
      },

      deleteFolder: (folderId: string) => {
        set((state) => {
          const newFolders = new Map(state.folders)
          newFolders.delete(folderId)
          return { folders: newFolders }
        })
      },

      renameFolder: (folderId: string, name: string) => {
        set((state) => {
          const newFolders = new Map(state.folders)
          const folder = newFolders.get(folderId)
          if (folder) {
            folder.name = name
            folder.updatedAt = Date.now()
          }
          return { folders: newFolders }
        })
      },

      addPostToFolder: (folderId: string, postId: string, post?: any) => {
        console.log("[Folders] Adding post to folder:", { folderId, postId, hasPost: !!post })
        set((state) => {
          const newFolders = new Map(state.folders)
          const folder = newFolders.get(folderId)
          if (folder) {
            folder.postIds.add(postId)
            if (post) {
              console.log("[Folders] Storing full post data")
              folder.posts.set(postId, post)
            } else {
              console.warn("[Folders] No post data provided!")
            }
            folder.updatedAt = Date.now()
          }
          return { folders: newFolders }
        })
      },

      removePostFromFolder: (folderId: string, postId: string) => {
        set((state) => {
          const newFolders = new Map(state.folders)
          const folder = newFolders.get(folderId)
          if (folder) {
            folder.postIds.delete(postId)
            folder.updatedAt = Date.now()
          }
          return { folders: newFolders }
        })
      },

      isPostInFolder: (folderId: string, postId: string) => {
        return get().folders.get(folderId)?.postIds.has(postId) ?? false
      },

      getPostFolders: (postId: string) => {
        return Array.from(get().folders.values()).filter((folder) => folder.postIds.has(postId))
      },

      getFolderPosts: (folderId: string) => {
        const folder = get().folders.get(folderId)
        if (!folder) {
          console.log("[Folders] Folder not found:", folderId)
          return []
        }
        
        console.log("[Folders] Getting posts for folder:", {
          folderId,
          postIdsCount: folder.postIds.size,
          postsMapSize: folder.posts.size,
          postIds: Array.from(folder.postIds),
        })
        
        // Return full post objects from the posts map
        const posts = Array.from(folder.postIds).map(postId => folder.posts.get(postId)).filter(Boolean)
        console.log("[Folders] Returning posts:", posts.length)
        return posts
      },

      // Crop management methods
      addCroppedImage: (folderId: string, postId: string, cropData: CropData, name?: string) => {
        const cropId = generateCropId()
        const croppedImage: CroppedImage = {
          id: cropId,
          postId,
          cropData,
          createdAt: new Date().toISOString(),
          name,
        }

        set((state) => {
          const newFolders = new Map(state.folders)
          const folder = newFolders.get(folderId)
          if (folder) {
            if (!folder.croppedImages) {
              folder.croppedImages = new Map()
            }
            const crops = folder.croppedImages.get(postId) || []
            crops.push(croppedImage)
            folder.croppedImages.set(postId, crops)
            // Always ensure postId is tracked so getCroppedImagePost can find it
            folder.postIds.add(postId)
            folder.updatedAt = Date.now()
          }
          return { folders: newFolders }
        })

        return cropId
      },

      removeCroppedImage: (folderId: string, cropId: string) => {
        set((state) => {
          const newFolders = new Map(state.folders)
          const folder = newFolders.get(folderId)
          if (folder && folder.croppedImages) {
            for (const [postId, crops] of folder.croppedImages.entries()) {
              const filtered = crops.filter((c) => c.id !== cropId)
              if (filtered.length !== crops.length) {
                if (filtered.length === 0) {
                  folder.croppedImages.delete(postId)
                } else {
                  folder.croppedImages.set(postId, filtered)
                }
                folder.updatedAt = Date.now()
                break
              }
            }
          }
          return { folders: newFolders }
        })
      },

      updateCroppedImage: (folderId: string, cropId: string, cropData: CropData) => {
        set((state) => {
          const newFolders = new Map(state.folders)
          const folder = newFolders.get(folderId)
          if (folder && folder.croppedImages) {
            for (const [postId, crops] of folder.croppedImages.entries()) {
              const cropIndex = crops.findIndex((c) => c.id === cropId)
              if (cropIndex !== -1) {
                crops[cropIndex].cropData = cropData
                folder.croppedImages.set(postId, crops)
                folder.updatedAt = Date.now()
                break
              }
            }
          }
          return { folders: newFolders }
        })
      },

      getCroppedImages: (folderId: string, postId: string) => {
        const folder = get().folders.get(folderId)
        return folder?.croppedImages?.get(postId) || []
      },

      getAllCroppedImages: (folderId: string) => {
        const folder = get().folders.get(folderId)
        if (!folder?.croppedImages) return []

        const allCrops: CroppedImage[] = []
        for (const crops of folder.croppedImages.values()) {
          allCrops.push(...crops)
        }
        return allCrops
      },
    }),
    {
      name: "folders",
      partialize: (state) => ({
        folders: Array.from(state.folders.entries()).map(([id, folder]) => [
          id,
          {
            ...folder,
            postIds: Array.from(folder.postIds),
            posts: Array.from(folder.posts.entries()),
            croppedImages: folder.croppedImages ? Array.from(folder.croppedImages.entries()) : [],
          },
        ]),
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as
          | {
              folders?: [
                string,
                Omit<ImageFolder, "postIds" | "posts" | "croppedImages"> & {
                  postIds: string[]
                  posts?: [string, any][]
                  croppedImages: [string, CroppedImage[]][]
                },
              ][]
            }
          | undefined

        const entries = persisted?.folders ?? []
        const foldersMap: Map<string, ImageFolder> = new Map(
          entries.map(([id, folder]) => [
            id,
            {
              ...folder,
              postIds: new Set(folder.postIds ?? []),
              posts: new Map(folder.posts ?? []), // Initialize empty if not present
              croppedImages: new Map(folder.croppedImages ?? []),
            },
          ]),
        )

        return {
          ...currentState,
          folders: foldersMap,
        }
      },
    },
  ),
)
